/**
 * useNativeShare — hook that exposes:
 *   - `canShare`: boolean, whether the browser supports the Web Share API
 *   - `share({ url, text, title, imageUrl })`: attempts to share; falls back
 *     to text-only share if the image can't be fetched or if files are not
 *     supported by the browser.
 *
 * On modern mobile browsers (Chrome Android, Safari iOS, Samsung Internet…)
 * this opens the native OS share sheet — one tap goes to WhatsApp, Telegram,
 * Messages, Email, AirDrop, etc. On desktop most browsers return false for
 * canShare so the calling component should hide the button gracefully.
 */
import { useCallback, useMemo } from "react";
import { toast } from "sonner";

export function useNativeShare() {
  const canShare = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    return typeof navigator.share === "function";
  }, []);

  const share = useCallback(async ({ url, text, title, imageUrl }) => {
    if (!canShare) return { ok: false, reason: "unsupported" };

    // Try to share the image as a File (yields the richest preview in
    // WhatsApp, Messages, etc.). Only attempt if we have an imageUrl AND
    // the browser supports canShare({ files: [...] }).
    let filePayload = null;
    if (imageUrl && typeof navigator.canShare === "function") {
      try {
        const res = await fetch(imageUrl, { credentials: "omit" });
        if (res.ok) {
          const blob = await res.blob();
          const ext = (blob.type.split("/")[1] || "png").split("+")[0];
          const filename = `crisangel-${Date.now()}.${ext}`;
          const file = new File([blob], filename, { type: blob.type });
          if (navigator.canShare({ files: [file] })) {
            filePayload = { files: [file] };
          }
        }
      } catch {
        /* ignore — will fallback to text/url share below */
      }
    }

    const basePayload = { title, text, url };

    // First attempt: with file
    if (filePayload) {
      try {
        await navigator.share({ ...basePayload, ...filePayload });
        return { ok: true, mode: "file" };
      } catch (e) {
        // User cancelled ("AbortError") — silent
        if (e?.name === "AbortError") return { ok: false, cancelled: true };
        // Any other error → try text-only fallback
      }
    }

    // Fallback: text + url only
    try {
      await navigator.share(basePayload);
      return { ok: true, mode: "text" };
    } catch (e) {
      if (e?.name === "AbortError") return { ok: false, cancelled: true };
      toast.error("No se pudo abrir el compartir del sistema");
      return { ok: false, error: e };
    }
  }, [canShare]);

  return { canShare, share };
}
